import {useState, useEffect, useMemo, useRef} from 'react'
import {useDebounce, useMap} from 'react-use'
import {isEmpty} from 'lodash'
import {
  RIF,
  sortBy,
  useCurrentProjectState,
  _,
  useCurrentWorkspaceState,
  RequestResult,
  loadSessionStorage,
  checkIsStaffUser,
} from '../../lib'
import {
  selectTheme,
  createDispatchActions,
  selectProjectData,
  selectWorkspaces,
  selectMethod,
  selectTempTaskData,
} from '../../store'
import {
  SetupProjectTag,
  MobileAppPreview,
  AboutProjectBlock,
  DeviceBlock,
  ParticipantTaskBlock,
  InstructionBlock,
  ContactBlock,
  ProjectSetupBar,
  TutorialPopup,
  SaveBar,
  ProjectDiscardPop,
  ProjectConfirmUpdatePop,
  PopupMissingDeviceTask,
  ParticipantTaskBlockRefType,
} from '..'
import {WarningIcon} from '../../asset/image/icon'
import {ProjectAuditTrailAction} from '../../shared/db'
import {
  getUpdatesForProjectContentFromProject,
  ProjectContent,
  TaskScheduleSettingType,
  TaskStateType,
  TempTask,
} from '../../model'
import {TaskType} from '../../shared/db'
import {PopupConfirmArchiveTask} from '../molecules/popup_confirm_archive_task'
import {PopupConfirmUnarchiveTask} from '../molecules/popup_confirm_unarchive_task'
import moment from 'moment'

export interface ScheduleTimeSlotItem {
  scheduleId: string
  startDate: string
  endDate: string | null
}

export const ProjectSetupPage = () => {
  const {pad, color, fontWeight} = selectTheme()

  const {
    doREQUEST_METHOD_FETCH,
    doTEMP_TASK_DATA_SET,
    doTEMP_TASK_DATA_MOVESENSE_DEVICE_ENABLE_UPDATE,
    doTEMP_TASK_DATA_GARMIN_STREAM_ENABLE_UPDATE,
    doTEMP_TASK_DATA_UPDATE_TASK,
    doREQUEST_METHOD_UPDATE,
    doREQUEST_DEXCOM_INTEGRATION_LIST_FETCH,
    doREQUEST_PROJECT_AUDIT_TRAIL_CREATE,
    doREQUEST_SCHEDULE_TIME_SLOT_LIST_GENERATE,
  } = createDispatchActions()

  const [requestResult, setRequestResult] = useState<RequestResult | null>(null)

  const projectState = selectProjectData()
  const loadSession = loadSessionStorage()
  const isStaffUser = checkIsStaffUser(loadSession?.identity)
  const {projectId, project} = useCurrentProjectState()
  const methodState = selectMethod()
  const garminDeviceTask: TaskStateType | undefined = _.find(methodState?.taskList, {type: TaskType.GarminDevice})
  const garminDeviceEnable =
    methodState?.garminDeviceEnable ||
    _.filter(garminDeviceTask?.garminDevice, (item) => {
      return typeof item === 'boolean' && item === true
    }).length > 0
  const methodId = project?.methodList?.[0].id || methodState?.id
  const tempTaskData = selectTempTaskData()
  const isEditingLiveProject = project?.status === 'live'

  const workspacesState = selectWorkspaces()
  const {workspaceId} = useCurrentWorkspaceState()
  const role = workspacesState.roleInWorkspaces[workspaceId ?? ''] || 'owner'

  // edit live project
  const [displayDiscardPopup, setDisplayDiscardPopup] = useState(false)
  const [displayConfirmUpdatePop, setDisplayConfirmUpdatePop] = useState(false)
  const [displayConfirmArchivePop, setDisplayConfirmArchivePop] = useState(false)
  const [displayConfirmUnarchivePop, setDisplayConfirmUnarchivePop] = useState(false)
  const currentArchiveTask = useRef<TempTask | null>(null)
  const currentUnarchiveTask = useRef<TempTask | null>(null)
  const participantTaskBlockRef = useRef<ParticipantTaskBlockRefType>()
  const [currentBlock, setCurrentBlock] = useState('aboutProject')

  // device
  const tempMovesenseDeviceEnable = tempTaskData.movesenseDeviceEnable
  const movesenseTasks: TaskStateType[] = _.filter(methodState?.taskList, {type: TaskType.StopwatchMovesenseStream})
  const tempMovesenseTasks: TempTask[] = _.filter(tempTaskData?.taskList, {
    type: TaskType.StopwatchMovesenseStream,
  })
  const hasMovesenseTasks: boolean = !!movesenseTasks.length || !!tempMovesenseTasks.length

  const tempGarminStreamEnable = tempTaskData.garminStreamEnable
  const garminStreamTasks: TaskStateType[] = _.filter(methodState?.taskList, {type: TaskType.StopwatchGarminStream})
  const tempGarminStreamTasks: TempTask[] = _.filter(tempTaskData?.taskList, {
    type: TaskType.StopwatchGarminStream,
  })
  const hasGarminStreamTasks: boolean = !!garminStreamTasks.length || !!tempGarminStreamTasks.length

  const [displayWarningDevicePop, setDisplayWarningDevicePop] = useState({
    [TaskType.StopwatchMovesenseStream]: false, //check_stopwatch_movesense_stream
    [TaskType.StopwatchGarminStream]: false,
  })

  const [displayFinishPopup, setDisplayFinishPopup] = useState(false)
  // show tutorial popup if nothing has been filled
  const [showingTutorial, setShowingTutorial] = useState(false)

  // when input completed change left checkIcon color
  const [aboutProjectValueFilled, setAboutProjectValueFilled] = useState(false)
  const [tasksFilled, setTasksFilled] = useState(false)
  const [contactValueFilled, setContactValueFilled] = useState(false)
  const [instructionValueFilled, setInstructionValueFilled] = useState(false)
  const finishedRequiredField = useMemo(() => {
    let result = true
    if (!aboutProjectValueFilled) result = false
    if (!tasksFilled) result = false
    if (!contactValueFilled) result = false
    if (!instructionValueFilled) result = false
    return result
  }, [aboutProjectValueFilled, tasksFilled, contactValueFilled, instructionValueFilled])

  // editing values
  const [projectContent, projectContentActions] = useMap<ProjectContent>()
  const projectContentUpdateTimestamp = useMemo(() => {
    for (const value of Object.values(projectContent)) {
      if (!isEmpty(value)) {
        return Date.now()
      }
    }
  }, [projectContent])

  useEffect(() => {
    // window.scrollTo({top: 0, behavior: 'smooth'})

    // only update project content if project has newer content than user input
    const projectUpdatedAt = project?.updatedAt ? new Date(project.updatedAt).getTime() : -1
    const projectContentUpdatedAt = projectContentUpdateTimestamp ?? -1
    if (projectUpdatedAt > projectContentUpdatedAt) {
      const projectContentUpdates = getUpdatesForProjectContentFromProject({
        projectContent,
        project,
      })
      if (projectContentUpdates.name) {
        projectContentActions.set('name', projectContentUpdates.name)
      }
      if (projectContentUpdates.description) {
        projectContentActions.set('description', projectContentUpdates.description)
      }
      if (projectContentUpdates.investigator) {
        projectContentActions.set('investigator', projectContentUpdates.investigator)
      }
      if (projectContentUpdates.organization) {
        projectContentActions.set('organization', projectContentUpdates.organization)
      }
      if (projectContentUpdates.instructionValue) {
        projectContentActions.set('instructionValue', projectContentUpdates.instructionValue)
      }
      if (projectContentUpdates.contactValue) {
        projectContentActions.set('contactValue', projectContentUpdates.contactValue)
      }
    }

    setShowingTutorial(false)
    if (project) {
      if (project.investigator) return
      if (project.organization) return
      if (project.description) return
      if (project.participantInstructions) return
      if (project.contactDescription) return
      if (Object.keys(projectState).length > 1) return
      setShowingTutorial(true)
    }
  }, [project])

  useEffect(() => {
    if (methodId) {
      doREQUEST_METHOD_FETCH({
        setRequestResult,
        payload: {
          methodId,
        },
      })
    }
  }, [methodId])

  useEffect(() => {
    if (workspaceId) {
      doREQUEST_DEXCOM_INTEGRATION_LIST_FETCH({
        payload: {
          workspaceId,
        },
      })
    }
  }, [workspaceId])

  useEffect(() => {
    if (!requestResult) return
    if (!requestResult.success) return
    if (!isEditingLiveProject) return
    const enabledTempTaskList = (methodState.taskList ?? [])
      .filter((item) => item.enabled)
      .sort(sortBy('index'))
      .map((task, index) => ({...task, index}))
    const hiddenTempTaskList = (methodState.taskList ?? [])
      .filter((item) => !item.enabled)
      .map((task, index) => ({...task, index}))
    const result = enabledTempTaskList.concat(hiddenTempTaskList)
    doTEMP_TASK_DATA_SET({
      taskList: result,
      garminDeviceEnable,
      garminConnectEnable: methodState.garminConnectEnable,
      garminStreamEnable: methodState.garminStreamEnable,
      movesenseDeviceEnable: methodState.movesenseDeviceEnable,
      dexcomIntegrationId: methodState.dexcomIntegrationId,
    })
  }, [requestResult])

  useEffect(() => {
    if (projectId && !isStaffUser) {
      doREQUEST_PROJECT_AUDIT_TRAIL_CREATE({
        payload: {
          projectId,
          action: ProjectAuditTrailAction.Read,
        },
      })
    }
  }, [projectId])

  const [startDate, setStartDate] = useState(new Date())
  const [simulationDuration, setSimulationDuration] = useState<number | string>(30)
  const [requestScheduleTimeSlotListGenerateResult, setRequestScheduleTimeSlotListGenerateResult] =
    useState<RequestResult | null>(null)

  const generateDateString = (date: Date) => {
    const y = date.getFullYear()
    const m = String(date.getMonth() + 1).padStart(2, '0')
    const d = String(date.getDate()).padStart(2, '0')
    return `${y}-${m}-${d}T00:00:00.000Z`
  }

  useEffect(() => {
    if (!methodState?.taskList) return
    if (!projectId) return
    if (methodState.taskList.filter(({scheduleList}) => scheduleList && scheduleList.length > 0).length === 0) return
    const scheduleIdList = methodState.taskList.flatMap(({scheduleList}) =>
      scheduleList?.filter(({inactive}) => !inactive).map(({id}) => id),
    )
    const duration = typeof simulationDuration === 'number' ? simulationDuration : 30
    doREQUEST_SCHEDULE_TIME_SLOT_LIST_GENERATE({
      setRequestResult: setRequestScheduleTimeSlotListGenerateResult,
      payload: {
        scheduleIdList: _.uniq(scheduleIdList),
        absoluteStartDate: generateDateString(startDate),
        startDate: generateDateString(startDate),
        endDate: generateDateString(moment(startDate).add(duration, 'days').toDate()),
        baseSeed: projectId,
      },
    })
  }, [methodState?.taskList, projectId, startDate, simulationDuration])

  const scheduleTimeSlotList: ScheduleTimeSlotItem[] = useMemo(() => {
    if (!requestScheduleTimeSlotListGenerateResult?.success) return []
    return (requestScheduleTimeSlotListGenerateResult.result.payload as ScheduleTimeSlotItem[]).map(
      ({startDate, endDate, ...rest}) => ({
        startDate: startDate.replace('Z', ''),
        endDate: endDate ? endDate.replace('Z', '') : null,
        ...rest,
      }),
    )
  }, [requestScheduleTimeSlotListGenerateResult])

  const onScroll = () => {
    const dh = document.documentElement.offsetHeight
    const wh = window.innerHeight
    const scrollTop = window.pageYOffset
    const scrollMiddle = scrollTop + (wh - 110) / 2
    const middleLineOffset = scrollMiddle / 100
    const yPercentage = (scrollTop / (dh - wh)) * 100 + 1
    if (middleLineOffset > scrollBarOffset.contacts || yPercentage >= 100) {
      setCurrentBlock('contacts')
    } else if (middleLineOffset > scrollBarOffset.instruction) {
      setCurrentBlock('instruction')
    } else if (middleLineOffset > scrollBarOffset.participantTask) {
      setCurrentBlock('participantTask')
    } else if (middleLineOffset > scrollBarOffset.device) {
      setCurrentBlock('device')
    } else if (middleLineOffset > scrollBarOffset.aboutProject) {
      setCurrentBlock('aboutProject')
    }
  }

  const blockList = ['aboutProject', 'device', 'participantTask', 'instruction', 'contacts']

  const scrollBarOffset: {[key: string]: number} = {
    aboutProject: 0,
    device: 7.3,
    participantTask: 12,
    instruction: 16.5,
    contacts: 20.2,
  }

  const handleMovesenseRemove = () => {
    if (isEditingLiveProject) {
      doTEMP_TASK_DATA_MOVESENSE_DEVICE_ENABLE_UPDATE({movesenseDeviceEnable: false})
      setDisplayWarningDevicePop((prev) => ({
        ...prev,
        stopwatch_movesense_stream: false, //check_stopwatch_movesense_stream
      }))
      setDisplayConfirmUpdatePop(true)
    }
    if (!isEditingLiveProject) {
      doREQUEST_METHOD_UPDATE({
        payload: {
          methodId,
          movesenseDeviceEnable: false,
        },
      })
      setDisplayWarningDevicePop((prev) => ({
        ...prev,
        stopwatch_movesense_stream: false, //check_stopwatch_movesense_stream
      }))
      if (
        displayWarningDevicePop.stopwatch_garmin_stream === false &&
        displayWarningDevicePop.stopwatch_movesense_stream === false //check_stopwatch_movesense_stream
      ) {
        setDisplayFinishPopup(true)
      }
    }
  }

  const handleGarminStreamRemove = () => {
    if (isEditingLiveProject) {
      doTEMP_TASK_DATA_GARMIN_STREAM_ENABLE_UPDATE({garminStreamEnable: false})
      setDisplayWarningDevicePop((prev) => ({
        ...prev,
        stopwatch_garmin_stream: false,
      }))
      setDisplayConfirmUpdatePop(true)
    }
    if (!isEditingLiveProject) {
      doREQUEST_METHOD_UPDATE({
        payload: {
          methodId,
          garminStreamEnable: false,
        },
      })
      setDisplayWarningDevicePop((prev) => ({
        ...prev,
        stopwatch_garmin_stream: false,
      }))
      if (
        displayWarningDevicePop.stopwatch_garmin_stream === false &&
        displayWarningDevicePop.stopwatch_movesense_stream === false //check_stopwatch_movesense_stream
      ) {
        setDisplayFinishPopup(true)
      }
    }
  }

  const handleClickLiveEditSave = () => {
    if (tempMovesenseDeviceEnable && !hasMovesenseTasks) {
      //check_stopwatch_movesense_stream
      return setDisplayWarningDevicePop((prev) => ({...prev, stopwatch_movesense_stream: true}))
    }
    if (tempGarminStreamEnable && !hasGarminStreamTasks)
      return setDisplayWarningDevicePop((prev) => ({...prev, stopwatch_garmin_stream: true}))
    return setDisplayConfirmUpdatePop(true)
  }

  const archiveTask = (task: TempTask) => {
    currentArchiveTask.current = task
    setDisplayConfirmArchivePop(true)
  }

  const unarchiveTask = (task: TempTask) => {
    currentUnarchiveTask.current = task
    setDisplayConfirmUnarchivePop(true)
  }

  const archiveAction = () => {
    if (currentArchiveTask.current) {
      const archiveTask = currentArchiveTask.current
      archiveTask.actionType = 'hide'
      archiveTask.enabled = false
      doTEMP_TASK_DATA_UPDATE_TASK(archiveTask)
    }
    if (participantTaskBlockRef.current) {
      participantTaskBlockRef.current.closeTaskEditor()
    }
    setDisplayConfirmArchivePop(false)
  }

  const unarchiveAction = () => {
    if (currentUnarchiveTask.current) {
      const unarchiveTask = currentUnarchiveTask.current
      unarchiveTask.actionType = 'show'
      unarchiveTask.enabled = true
      doTEMP_TASK_DATA_UPDATE_TASK(unarchiveTask)

      if (participantTaskBlockRef.current) {
        participantTaskBlockRef.current.editTask(currentUnarchiveTask.current)
      }
    }

    console.log('setDisplayConfirmUnarchivePop')
    setDisplayConfirmUnarchivePop(false)
  }

  window.addEventListener('scroll', onScroll)

  return (
    <>
      {RIF(
        isEditingLiveProject,
        <>
          <SaveBar
            {...{
              disabled: !finishedRequiredField,
              buttonText: 'Save and Update',
              closeAction: () => setDisplayDiscardPopup(true),
              handleClickSave: handleClickLiveEditSave,
              html: (
                <div
                  onClick={() => setDisplayDiscardPopup(true)}
                  css={{
                    marginRight: '20px',
                    color: color.warning,
                    cursor: 'pointer',
                  }}>
                  Discard
                </div>
              ),
            }}
            css={{boxShadow: 'none'}}
          />

          <div
            css={{
              background: color.warning,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '40px',
              color: color.white,
              position: 'fixed',
              zIndex: 101,
              width: '100%',
              left: 0,
              top: '50px',
            }}>
            You are editing a live project--until you click Save and Update, participants will not see your changes.
          </div>
        </>,
      )}
      <div
        css={{
          paddingTop: pad.xl,
          paddingLeft: '19vw',
          paddingBottom: '7.857rem',
          background: color.background,
          width: '100%',
          minHeight: 'calc(100vh - 50px)',
          marginTop: isEditingLiveProject ? '90px' : '50px',
          display: 'flex',
        }}
        onScroll={onScroll}>
        <div
          css={{
            position: 'fixed',
            left: '30px',
            top: isEditingLiveProject ? '135' : '85px',
            display: 'flex',
            flexDirection: 'column',
          }}>
          <div css={{width: '190px', marginBottom: '24px'}}>
            <div
              css={{
                fontSize: '20px',
                fontWeight: '700',
                marginBottom: '8px',
              }}>
              {isEditingLiveProject ? 'Edit Live Project' : 'App Builder'}
            </div>
            <div>
              {isEditingLiveProject
                ? 'You can change some of the content. Some editing actions may be limited.'
                : 'Create and customize the participant mobile app for your research project.'}
            </div>
          </div>

          {blockList.map((item, index) => {
            return (
              <SetupProjectTag
                key={index}
                {...{
                  blockName: item,
                  scrollBarOffset: scrollBarOffset[item],
                  currentBlock,
                  setCurrentBlock: (block: string) => setCurrentBlock(block),
                  aboutProjectValueFilled,
                  tasksFilled,
                  contactValueFilled,
                  instructionValueFilled,
                }}
              />
            )
          })}
        </div>
        <div>
          {RIF(
            role === 'viewer',
            <div
              css={{
                width: '55vw',
                height: '40px',
                borderRadius: '5px',
                background: 'rgba(241, 144, 110, 0.2)',
                display: 'flex',
                alignItems: 'center',
                paddingLeft: '18px',
                marginBottom: pad.slightlyLarger,
              }}>
              <img
                width={20}
                src={WarningIcon}
                alt=''
                aria-hidden
                css={{
                  marginRight: pad.mediumSmall,
                }}
              />
              <p
                css={{
                  fontWeight: fontWeight.medium,
                }}>
                Sorry - Your current access level does not allow you to edit project.
              </p>
            </div>,
          )}
          <AboutProjectBlock
            {...{
              aboutProjectValue: projectContent,
              onChange: (value) => {
                projectContentActions.set('name', value.name)
                projectContentActions.set('description', value.description)
                projectContentActions.set('investigator', value.investigator)
                projectContentActions.set('organization', value.organization)
              },
              onValidated: () => {
                setAboutProjectValueFilled(true)
              },
              onError: () => {
                setAboutProjectValueFilled(false)
              },
            }}
          />

          <DeviceBlock
            {...{
              onAddScheduledMovesenseTask: () => {
                participantTaskBlockRef.current?.addTask(
                  TaskType.StopwatchMovesenseStream,
                  TaskScheduleSettingType.Schedule,
                )
              },
              onAddEventTrackerMovesenseTask: () => {
                participantTaskBlockRef.current?.addTask(
                  TaskType.StopwatchMovesenseStream,
                  TaskScheduleSettingType.EventTracker,
                )
              },
            }}
          />

          <ParticipantTaskBlock
            ref={participantTaskBlockRef}
            {...{
              onTaskFilledChange: (filled) => setTasksFilled(filled),
              scheduleTimeSlotList,
              startDate,
              setStartDate,
              simulationDuration,
              setSimulationDuration,
              archiveTask,
              unarchiveTask,
            }}
          />

          <InstructionBlock
            {...{
              instructionValue: projectContent.instructionValue,
              onChange: (value) => {
                projectContentActions.set('instructionValue', value)
              },
              onValidated: () => {
                setInstructionValueFilled(true)
              },
              onError: () => {
                setInstructionValueFilled(false)
              },
            }}
          />

          <ContactBlock
            {...{
              contactValue: projectContent.contactValue,
              onChange: (value) => {
                projectContentActions.set('contactValue', value)
              },
              onValidated: () => {
                setContactValueFilled(true)
              },
              onError: () => {
                setContactValueFilled(false)
              },
            }}
          />
        </div>
        <MobileAppPreview
          {...{
            projectContent,
            currentBlock,
            isEditingLiveProject,
          }}
        />
      </div>

      {RIF(
        role !== 'viewer',
        <ProjectSetupBar
          {...{
            projectContent,
            finishedRequiredField,
            isEditingLiveProject,
            setDisplayWarningDevicePop,
            displayFinishPopup,
            setDisplayFinishPopup,
          }}
        />,
      )}

      {RIF(showingTutorial, <TutorialPopup setShowingTutorial={setShowingTutorial} />)}

      {RIF(displayDiscardPopup, <ProjectDiscardPop closeAction={() => setDisplayDiscardPopup(false)} />)}

      {RIF(
        displayConfirmUpdatePop,
        <ProjectConfirmUpdatePop
          projectContent={projectContent}
          closeAction={() => setDisplayConfirmUpdatePop(false)}
        />,
      )}

      {RIF(
        displayConfirmArchivePop,
        <PopupConfirmArchiveTask
          closeAction={() => setDisplayConfirmArchivePop(false)}
          archiveAction={() => archiveAction()}
        />,
      )}

      {RIF(
        displayConfirmUnarchivePop,
        <PopupConfirmUnarchiveTask
          closeAction={() => setDisplayConfirmUnarchivePop(false)}
          unarchiveAction={() => unarchiveAction()}
        />,
      )}

      {RIF(
        displayWarningDevicePop[TaskType.StopwatchMovesenseStream],
        <PopupMissingDeviceTask
          {...{
            deviceTaskType: TaskType.StopwatchMovesenseStream,
            closeAction: handleMovesenseRemove,
            exitAction: () => setDisplayWarningDevicePop((prev) => ({...prev, stopwatch_movesense_stream: false})),
            onAddScheduledTask: () => {
              participantTaskBlockRef.current?.addTask(
                TaskType.StopwatchMovesenseStream,
                TaskScheduleSettingType.Schedule,
              )
            },
            onAddEventTracker: () => {
              participantTaskBlockRef.current?.addTask(
                TaskType.StopwatchMovesenseStream,
                TaskScheduleSettingType.EventTracker,
              )
            },
          }}
        />,
      )}

      {RIF(
        displayWarningDevicePop[TaskType.StopwatchGarminStream],
        <PopupMissingDeviceTask
          {...{
            deviceTaskType: TaskType.StopwatchGarminStream,
            closeAction: handleGarminStreamRemove,
            exitAction: () => setDisplayWarningDevicePop((prev) => ({...prev, stopwatch_garmin_stream: false})),
            onAddAccStreamingTask: () => {
              participantTaskBlockRef.current?.addTask(TaskType.StopwatchGarminStream, TaskScheduleSettingType.Schedule)
            },
          }}
        />,
      )}
    </>
  )
}
